/**
 * Geo-location utility functions for displaying location data
 */
import * as Sentry from '@sentry/react'

import { createLogger } from './logger'

const logger = createLogger('geoUtils')

/**
 * Convert ISO 3166-1 alpha-2 country code to flag emoji
 * @param {string} countryCode - Two-letter country code (e.g., "US", "CA")
 * @returns {string} Flag emoji or globe emoji if invalid/missing
 */
export function getCountryFlag(countryCode) {
  if (!countryCode || countryCode.length !== 2) {
    return '🌐'
  }

  try {
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map((char) => 127397 + char.charCodeAt(0))

    return String.fromCodePoint(...codePoints)
  } catch (error) {
    // Report to error tracking service
    Sentry.captureException(error, { extra: { countryCode } })
    logger.warn('Failed to generate flag for country code:', countryCode, error)
    return '🌐'
  }
}

/**
 * Format location string from geo data object
 * @param {Object} geo - Geo object with city, region, countryName, country
 * @param {string} [geo.city] - City name
 * @param {string} [geo.region] - Region/state name
 * @param {string} [geo.countryName] - Full country name
 * @param {string} [geo.country] - Country code
 * @returns {string} Formatted location string (e.g., "San Francisco, California, United States")
 */
export function formatLocation(geo) {
  if (!geo) return 'Unknown'

  const parts = [geo.city, geo.region, geo.countryName || geo.country].filter(Boolean)

  return parts.length > 0 ? parts.join(', ') : 'Unknown'
}

/**
 * Format short location (city + country only)
 * @param {Object} geo - Geo object
 * @returns {string} Short location string (e.g., "San Francisco, US")
 */
export function formatShortLocation(geo) {
  if (!geo) return 'Unknown'

  const parts = [geo.city, geo.countryName || geo.country].filter(Boolean)

  return parts.length > 0 ? parts.join(', ') : 'Unknown'
}

/**
 * Format IP address for display
 * @param {string} ip - IP address
 * @param {boolean} [mask=false] - Whether to partially mask the IP
 * @returns {string} Formatted IP address
 */
export function formatIpAddress(ip, mask = false) {
  if (!ip) return 'Unknown'

  if (mask) {
    // IPv4: xxx.xxx.xxx.xxx
    const ipv4Parts = ip.split('.')
    if (ipv4Parts.length === 4) {
      return `${ipv4Parts[0]}.${ipv4Parts[1]}.***.***`
    }

    // IPv6: xxxx:xxxx:xxxx:xxxx:xxxx:xxxx:xxxx:xxxx (or abbreviated)
    if (ip.includes(':')) {
      const ipv6Parts = ip.split(':')
      // Show first 2 groups, mask the rest
      const visible = ipv6Parts.slice(0, 2).join(':')
      return `${visible}:****:****`
    }
  }

  return ip
}

/**
 * Get display-ready geo info object
 * @param {Object} geo - Raw geo data from API
 * @returns {Object} Display-ready geo info
 */
export function getGeoDisplayInfo(geo) {
  if (!geo || !geo.country) {
    return {
      flag: '🌐',
      location: 'Unknown',
      shortLocation: 'Unknown',
      hasGeo: false
    }
  }

  return {
    flag: getCountryFlag(geo.country),
    location: formatLocation(geo),
    shortLocation: formatShortLocation(geo),
    country: geo.countryName || geo.country,
    city: geo.city || null,
    region: geo.region || null,
    ip: geo.ipAddress || null,
    hasGeo: true
  }
}
